import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  DragModifiers,
  DragSelectionDelegate,
  Filter,
  GridIndexPath,
  GridViewDataSource,
  GridViewDelegate,
  IndexPath,
  ItemSelectionStore,
  UniversalFilterStore,
  VirtualScrollDelegate,
} from '@shared/ui';
import { isMacPlatform } from '@shared/utils';
import { DragSelectionStateController } from './drag-selection-state.controller';
import { RangeExtensionSessionController } from './range-extension-session.controller';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, skip, take, takeUntil } from 'rxjs/operators';
import { MosaicGalleryFacade } from './mosaic-gallery.facade';
import { concatLatestFrom } from '@ngrx/operators';
import { MosaicGalleryPanelDelegate } from '../../stores';
import { FilterConfigDelegate } from '@shared/ui';
import { FilterCriterion } from '@analysis/domain';
import { MosaicCellData } from '../mosaic-cell';
import {
  FilterAutocompleteEffects,
  MosaicRoiFeatureEffects,
  RoiFilteringEffects,
  RoiRevertEffects,
} from '../../effects';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MosaicGalleryRowNumberData } from '../mosaic-row-number';
import { MosaicGalleryHeaderData } from '../mosaic-section-header';

/**
 * TODO:
 * - extract filter config into separate smartcomponent
 * - test
 */
@Component({
  selector: 'ts-mosaic-gallery',
  templateUrl: './mosaic-gallery.component.html',
  styleUrls: ['./mosaic-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    UniversalFilterStore,
    ItemSelectionStore,
    MosaicGalleryFacade,
    FilterAutocompleteEffects,
    RoiRevertEffects,
    MosaicRoiFeatureEffects,
    RoiFilteringEffects,
    DragSelectionStateController,
    RangeExtensionSessionController,
  ],
})
export class MosaicGalleryComponent
  implements
    OnChanges,
    OnDestroy,
    AfterViewInit,
    GridViewDelegate<MosaicCellData>,
    MosaicGalleryPanelDelegate,
    FilterConfigDelegate,
    GridViewDataSource<MosaicCellData, MosaicGalleryRowNumberData, MosaicGalleryHeaderData | undefined>,
    DragSelectionDelegate<GridIndexPath>,
    VirtualScrollDelegate
{
  @Input() panelId!: number;
  @Input() sampleId!: string;
  @Input() loading = true;
  @Input()
  get editingEnabled(): boolean {
    return this.facade.editingEnabled;
  }
  set editingEnabled(value: boolean) {
    this.facade.setEditingEnabled(value);
  }
  @Input() syncing = false; // REVIEW: consider eliminating
  @Input() connectedDropLists: string[];
  @Input() scale = 1;
  @Input() set roiBoxVisible(visible: boolean) {
    this.facade.setRoiBoxVisible(visible);
  }
  @Input() removePanelDisabled = false;
  @Input() focused = false;

  @Output() roiFocused = new EventEmitter<string>();
  @Output() connectedDropListChanged = new EventEmitter<string[]>();
  @Output() roiDoubleClicked = new EventEmitter<string>();
  @Output() panelCloseRequested = new EventEmitter<void>();

  @ViewChild('contextMenuTemplate') contextMenuTemplate: TemplateRef<unknown>;

  @HostBinding('attr.id')
  get mosaicGalleryHostElementId(): string {
    return this.panelId != null && Number.isFinite(this.panelId) ? String(this.panelId) : '';
  }

  public readonly searchFields = this.facade.searchFields;

  public readonly selectedItems$ = this.facade.selectedItems$;
  public readonly selectedConfig$ = this.facade.selectedConfig$;
  public readonly configs$ = this.facade.configs$;
  public readonly filters$ = this.facade.filters$;
  public readonly contextMenu$ = this.facade.contextMenu$;
  public readonly draggingDisabled$ = this.facade.draggingDisabled$;
  public readonly dragSelectionEnabled$ = this.facade.dragSelectionEnabled$;

  public readonly cellSize = 128;
  public readonly cellGap = 4;
  public readonly rowNumeralWidth = 40;
  public readonly paddingNodes = 1;
  public readonly scrollThreshold = 0.2;

  private readonly destroy$ = new Subject<void>();

  private cellsPerRow: number | undefined = undefined;
  private hoverLabel: string | undefined = '';

  private overlayRef: OverlayRef | null = null;

  private resizeObserver = new ResizeObserver(() => {
    this.updateCellsPerRow();
    this._cdr.detectChanges();
  });

  constructor(
    private readonly _cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly facade: MosaicGalleryFacade,
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly dragSelectionController: DragSelectionStateController,
    private readonly rangeExtensionSession: RangeExtensionSessionController
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    let shouldUpdatePanelId = false;
    let shouldUpdateScale = false;
    let shouldLoadMore = false;

    for (const key in changes) {
      const { currentValue, previousValue } = changes[key];

      if (currentValue === previousValue) continue;

      switch (true) {
        case key === 'panelId':
          shouldUpdatePanelId = true;
          break;
        case key === 'scale':
          shouldUpdateScale = true;
          break;
        case key === 'loading':
          shouldLoadMore = currentValue === false;
          break;
        default:
          break;
      }
    }

    if (shouldUpdatePanelId) this.updatePanelId();
    if (shouldUpdateScale) this.updateCellsPerRow();
    if (shouldLoadMore) this.loadIfNeeded();
  }

  public ngAfterViewInit(): void {
    this.resizeObserver.observe(this.elementRef.nativeElement);
    this.updateCellsPerRow();
    this.wireLoadingAddicionalItems();
    this.wireRangeExtensionSession();
  }

  public ngOnDestroy(): void {
    this.facade.unregisterPanelRef(this.panelId, this);
    this.closeContextMenu();
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeObserver.disconnect();
  }

  public upsertSearchFilter(f: Filter): void {
    this.facade.upsertSearchFilter(f);
  }

  public applyFilterConfig(configId: string, filters: FilterCriterion[]): void {
    this.facade.applyFilterConfig(configId, filters);
  }

  private showContextMenu(event: PointerEvent) {
    if (!this.editingEnabled) return;

    this.closeContextMenu();

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo({ x: event.clientX, y: event.clientY })
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
        { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
        { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });

    this.overlayRef
      .outsidePointerEvents()
      .pipe(skip(1))
      .subscribe(() => this.closeContextMenu());

    const portal = new TemplatePortal(this.contextMenuTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);
  }

  public closeContextMenu(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.hoverLabel = undefined;
  }

  @HostListener('document:keydown.escape')
  public deselectAll(): void {
    if (!this.focused) return;
    this.closeContextMenu();
    this.clearSelection();
  }

  public menuOptionSelected(event: string): void {
    this.facade.addLabels([event]);
    this.clearSelection();
    this.closeContextMenu();
  }

  public menuOptionDeselected(event: string): void {
    this.facade.removeLabel(event);
    this.clearSelection();
    this.closeContextMenu();
  }

  public loadMore(): void {
    if (this.loading) return;
    this.facade.loadMore(this.sampleId);
  }

  public scrollThresholdReached(): void {
    this.loadMore();
  }

  public revertSelectedRoiChanges(): void {
    this.facade.revertSelectedRoiChanges();
    this.clearSelection();
  }

  public clearSelection(): void {
    this.facade.clearSelection();
  }

  public setMultiselectionEnabled(enabled: boolean): void {
    this.facade.setMultiselectionEnabled(enabled);
  }

  public setDragSelectionEnabled(enabled: boolean): void {
    this.facade.setDragSelectionEnabled(enabled);
  }

  //#region FilterConfigDelegate

  public clearAllFilters(): void {
    this.facade.clearAllFilters();
  }

  public removeActiveFilter(filter: Filter): void {
    this.facade.removeFilter(filter);
  }

  public createFilterConfig(filters: Filter[]): void {
    this.facade.createFilterConfig(this, filters);
  }

  public updateFilterConfig(name: string, filters: Filter[]): void {
    this.facade.saveFilterConfig(name, filters);
  }

  public deleteFilterConfig(configId: string): void {
    this.facade.deleteFilterConfig(configId);
  }

  //#endregion

  //#region GridViewDataSource

  public numberOfSections(): Observable<number> {
    return this.facade.numberOfSections();
  }

  public numberOfCellsInSection(section: number): Observable<number> {
    return this.facade.numberOfCellsInSection(section);
  }

  public getCellForIndexPath(indexPath: GridIndexPath): Observable<MosaicCellData> {
    return this.facade.getCellForIndexPath(indexPath, this.cellsPerRow, this.hoverLabel);
  }

  public getRowNumberForIndexPath(indexPath: IndexPath): Observable<MosaicGalleryRowNumberData> {
    return this.facade.getRowNumberForIndexPath(indexPath, this.cellsPerRow);
  }

  public getHeaderForIndexPath(index: number): Observable<MosaicGalleryHeaderData | undefined> {
    return this.facade.getHeaderForIndexPath(index);
  }

  //#endregion

  //#region GridViewDelegate

  public cellMoved(indexPath: IndexPath): void {
    this.facade.addLabelsToFocusedItems(indexPath);
    this.clearSelection();
  }

  public cellDraggingStarted(_indexPath: GridIndexPath, cell: MosaicCellData): void {
    this.closeContextMenu();

    if (!cell.selected) this.facade.selectItem(cell.roiId);
  }

  public cellClicked(_indexPath: GridIndexPath, cell: MosaicCellData) {
    this.facade.toggleItem(cell.roiId);
    this.closeContextMenu();
    this.roiFocused.emit(cell.roiId);
  }

  public cellDoubleClicked(_indexPath: GridIndexPath, cell: MosaicCellData) {
    this.facade.selectItem(cell.roiId);
    this.closeContextMenu();
    this.roiDoubleClicked.emit(cell.roiId);
  }

  public cellContextMenu(_indexPath: GridIndexPath, cell: MosaicCellData, pointerEvent: PointerEvent): void {
    pointerEvent.preventDefault();
    if (!cell.selected) this.facade.selectItem(cell.roiId);
    this.showContextMenu(pointerEvent);
  }

  public cellsRangeSelected(indexPaths: GridIndexPath[]): void {
    this.rangeExtensionSession.update({ indexPaths, cellsPerRow: this.cellsPerRow });
  }

  public cellIdentity(cell: MosaicCellData): string {
    return cell.roiId;
  }

  public cellCreated(data: MosaicCellData): void {
    this.facade.loadBlob(data.roiId);
    this.facade.loadMoreRoiLabels(data.roiId);
  }

  public cellDestroyed(data: MosaicCellData): void {
    this.facade.cancelLoadBlob(data.roiId);
    this.facade.cancelLoadMoreRoiLabels(data.roiId);
  }

  //#endregion

  //#region DragSelectionDelegate

  public dragSelectionStarted(_modifiers: DragModifiers): void {
    this.dragSelectionController.start();
  }

  public dragSelectionUpdated(live: GridIndexPath[], modifiers: DragModifiers): void {
    if (!this.cellsPerRow) return;
    this.dragSelectionController.update({ indexPaths: live, cellsPerRow: this.cellsPerRow, modifiers });
  }

  public dragSelectionCommitted(live: GridIndexPath[], modifiers: DragModifiers): void {
    if (!this.cellsPerRow) return;
    this.dragSelectionController.commit({ indexPaths: live, cellsPerRow: this.cellsPerRow, modifiers });
  }

  public dragSelectionCancelled(): void {
    this.dragSelectionController.cancel();
  }

  //#endregion

  //#region MosaicGalleryPanelDelegate

  public filterByLabel(label: string): void {
    this.facade.filterByLabel(label);
  }

  public filterByUnlabeled(): void {
    this.facade.filterByUnlabeled();
  }

  public filterByAsset(assetId: string): void {
    this.facade.filterByAsset(assetId);
  }

  public filterByChanged(): void {
    this.facade.filterByChanged();
  }

  public filterBySelected(): void {
    this.facade.filterBySelected();
  }

  public selectRois(roiIds: string[]): void {
    this.facade.selectRois(roiIds);
  }

  public addLabels(labels: string[]): void {
    this.facade.addLabels(labels);
  }

  public removeAllLabelsOnSelectedItems(): void {
    this.facade.removeAllLabelsOnSelectedItems();
    this.clearSelection();
  }

  //#endregion

  public onGalleryMouseDown(event: MouseEvent): void {
    this.facade.focusPanel(this.panelId);

    switch (true) {
      case this.isMultiselectionModifier(event):
        this.setMultiselectionEnabled(true);
        break;
      case event.shiftKey:
        this.setDragSelectionEnabled(true);
        break;
      default:
        break;
    }
  }

  private isMultiselectionModifier(event: MouseEvent): boolean {
    return isMacPlatform() ? event.metaKey : event.ctrlKey;
  }

  public getCellSize(): number {
    return this.cellSize * this.scale;
  }

  public getCellsPerRow(): number {
    return this.cellsPerRow ?? 0;
  }

  public setHoverLabel(label: string): void {
    this.hoverLabel = label;
  }

  private updateCellsPerRow() {
    const componentWidth = this.elementRef?.nativeElement?.clientWidth ?? window.innerWidth;
    this.cellsPerRow = Math.max(Math.floor((componentWidth - this.cellSize / 3) / (this.cellSize * this.scale)), 0);
  }

  private updatePanelId(): void {
    this.facade.setPanelRef(this.panelId, this);
  }

  private wireLoadingAddicionalItems(): void {
    this.facade.sectionCellCounts$
      .pipe(
        concatLatestFrom(() => [this.facade.filteredLabelIndex$]),
        filter(([, labelIndex]) => this.shouldLoadMore(labelIndex)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.loadMore());
  }

  private wireRangeExtensionSession(): void {
    this.facade.dragSelectionEnabled$
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((enabled) => (enabled ? this.rangeExtensionSession.start() : this.rangeExtensionSession.end()));
  }

  private loadIfNeeded() {
    this.facade.filteredLabelIndex$
      .pipe(
        take(1),
        filter((labelIndex) => this.shouldLoadMore(labelIndex))
      )
      .subscribe(() => this.loadMore());
  }

  private shouldLoadMore(labelIndex: Map<string, Set<string>>): boolean {
    const el = this.elementRef.nativeElement;

    if (!this.cellsPerRow) return false;

    const rowHeight = this.cellSize * this.scale + this.cellGap;
    const viewportHeight = el?.clientHeight ?? 0;
    const visibleRows = Math.ceil(viewportHeight / rowHeight);
    const minRoiCount = (visibleRows + this.paddingNodes) * Math.max(this.cellsPerRow, 1);

    const roiCount = Array.from(labelIndex.values()).reduce((acc, items) => acc + (items?.size ?? 0), 0);

    return minRoiCount >= roiCount;
  }
}
